import PropTypes from 'prop-types';
import React, {Component} from 'react';
import classNames from 'classnames';
import './QuantitySelector.less';
import {ButtonIcon} from '@homeaway/react-buttons';
import {MINUS__16, PLUS__16} from '@homeaway/svg-defs';

class QuantitySelector extends Component {
    static propTypes = {
        /** id attribute for QuantitySelector, required for a11y */
        id: PropTypes.string.isRequired,
        /** Label for the Quantity Selector (ex: 0 Children); This can be a string, element, or component. Required for a11y. */
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]).isRequired,
        /** Optional data required by custom label component */
        labelData: PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            label: PropTypes.string
        }),
        /** value for the number input */
        value: PropTypes.oneOfType([
            PropTypes.number.isRequired,
            PropTypes.string.isRequired
        ]),
        /** min attribute for the number input */
        min: PropTypes.number,
        /** max attribute for the number input */
        max: PropTypes.number,
        /** how much to increment/decrement per button click*/
        step: PropTypes.number,
        /** Label for the Decrement Button, required for a11y */
        decreaseLabel: PropTypes.string.isRequired,
        /** Label for the Increment Button, required for a11y */
        increaseLabel: PropTypes.string.isRequired,
        /** event handler to fire when the number increases */
        onIncrement: PropTypes.func.isRequired,
        /** event handler to fire when the number decreases */
        onDecrement: PropTypes.func.isRequired,
        /** Boolean to disable the focus/underline/keyboard input functionality of the input field */
        disableInput: PropTypes.bool
    }

    static defaultProps = {
        value: 0,
        min: 0,
        max: 20,
        step: 1,
        disableInput: false
    }

    constructor(props) {
        super(props);
    }

    stepUp = () => {
        const newValue = Number(this.props.value) + Number(this.props.step);

        if (newValue <= this.props.max) {
            this.handleChange(newValue);
        }
    }

    stepDown = () => {
        const newValue = Number(this.props.value) - Number(this.props.step);

        if (newValue >= this.props.min) {
            this.handleChange(newValue);
        }
    }

    // handle a11y users editing the text using AT
    handleA11y = () => {
        const {min, max} = this.props;
        let newValue = this.input.value || 0;

        // Remove possible leading zeros from input value
        if (newValue) {
            newValue = parseInt(newValue, 10);
            this.input.value = newValue;
        }

        if (newValue >= min && newValue <= max) {
            this.handleChange(newValue);
        }
    }

    handleChange = (newValue) => {
        if (newValue > this.props.value) {
            this.props.onIncrement(newValue);
        } else {
            this.props.onDecrement(newValue);
        }
    }

    renderLabelComponent = () => {
        const {label, labelData} = this.props;
        const LabelComponent = label;

        return (
            <span className="quantity-selector__custom-label">
                <LabelComponent data={labelData}/>
            </span>
        );
    }

    render() {
        const {id, label, labelData, min, max, value, decreaseLabel, increaseLabel, className, onIncrement, onDecrement, disableInput, ...rest} = this.props; // eslint-disable-line no-unused-vars
        const selectorClasses = classNames('quantity-selector', className);
        const isLabelComponent = typeof label === 'function';

        return (
            <div className={selectorClasses} {...rest}>
                <label aria-live="polite" className="quantity-selector__label text-muted" htmlFor={id}>
                    {isLabelComponent ? this.renderLabelComponent() : label}
                    <input disabled={disableInput} type="number" min={min} max={max} ref={(ref) => (this.input = ref)} className="quantity-selector__input" id={id} value={value} onChange={this.handleA11y}/>
                    <div className="quantity-selector__focus-indicator bg-brand" />
                </label>
                <ButtonIcon disabled={(value <= min)} label={decreaseLabel} onClick={this.stepDown} className="quantity-selector__btn quantity-selector__btn--decrease" svgMarkup={MINUS__16} circle/>
                <ButtonIcon disabled={(value >= max)} label={increaseLabel} onClick={this.stepUp} className="quantity-selector__btn quantity-selector__btn--increase" svgMarkup={PLUS__16} circle/>
            </div>
        );
    }
}

export default QuantitySelector;
